import { IPageConfigDataModel } from '../../data-models/page-config.data-model';
import { COMPANY_VIEW_TYPE, OriginViewDataModel } from '../../data-models/view-config.data-model';
import { IViewDataModel, CreateViewPayload, IViewListItemDataModel } from '../../data-models/view.data-model';
import {
  ViewModel,
  ViewSummaryModel,
  deserializeView,
  deserializeViewSummary,
} from '../../models/view.model';
import { ColumnMeta, RawPageConfig } from '../../types';
import { FeatureFlags } from '../../util/feature-flags';
import { getLdFlag } from '../../util/ld-client';
import { MaggieHttp } from '../MaggieHttp';
import { UnSharePayload } from './MaggieUploadQueries';

export type SharePayload = string[];

export async function deleteView(view: OriginViewDataModel) {
  await MaggieHttp.maggie.delete<number>(`/view/${view.id}`, {
    params: view.isDefault ? { force: true } : undefined,
  });

  return view;
}

export async function duplicateView(viewPayload: CreateViewPayload) {
  const { data } = await MaggieHttp.maggie.post<IViewDataModel, IViewDataModel>('/view', viewPayload);

  return deserializeView(data);
}

export async function shareView(id: number, emails: SharePayload) {
  const { data } = await MaggieHttp.maggie.put<SharePayload, IViewDataModel>(`/view/share/${id}`, emails);

  return data?.sharing?.map((user) => user.userId) ?? [];
}

export async function unshareView(id: number, emails: UnSharePayload) {
  const { data } = await MaggieHttp.maggie.put<UnSharePayload, number>(`/view/unshare/${id}`, emails);

  return data;
}
export async function updateView(viewDataModel: IViewDataModel): Promise<ViewModel> {
  const { data } = await MaggieHttp.maggie.put<IViewDataModel, IViewDataModel>(
    `/view/${viewDataModel.id}`,
    viewDataModel
  );

  return deserializeView(data);
}

/** The views for navigation and ordering, without their settings: {@link fetchView} has those for the view that opens. */
export async function fetchViews(): Promise<ViewSummaryModel[]> {
  // TODO: Remove useNewViewsP once it is enabled for all clients.
  const useNewViewsP = getLdFlag(FeatureFlags.useNewViewsP, false);
  const { data } = await MaggieHttp.maggie.get<IViewListItemDataModel[]>('/view', {
    params: {
      useNewViewsP,
      includeConfig: false,
    },
  });
  return data.map((viewDataModel) => deserializeViewSummary(viewDataModel));
}

export async function fetchView(id: number): Promise<ViewModel> {
  // TODO: Remove the params once useNewViewsP is enabled for all clients.
  const useNewViewsP = getLdFlag(FeatureFlags.useNewViewsP, false);
  const { data } = await MaggieHttp.maggie.get<IViewDataModel>(`/view/${id}`, {
    params: {
      useNewViewsP,
    },
    // The landing page asks for the URL's view before the list confirms the user has it, so a stale
    // bookmark answers 403 or 404 here; the page shows Not Found from the list, so don't report it.
    skipSentryStatuses: [403, 404],
  });
  return deserializeView(data);
}

export async function updatePageConfig(pageConfig: IPageConfigDataModel): Promise<IPageConfigDataModel> {
  const payload = {
    config: pageConfig,
  };
  const { data } = await MaggieHttp.maggie.put<RawPageConfig, RawPageConfig>(`/view/config`, payload);

  const config = data?.config;
  return config;
}

export async function fetchPageConfig(): Promise<IPageConfigDataModel> {
  const { data } = await MaggieHttp.maggie.get<RawPageConfig>(`/view/config`);
  const config = data?.config ?? {};

  if (config?.order) {
    config.order = Array.from(new Set(config?.order ?? []));
  }

  return config;
}

export async function fetchColumns(companyViewType: COMPANY_VIEW_TYPE | undefined): Promise<ColumnMeta[]> {
  // Opt into fieldDisplayConfig-curated KPI columns; without the param the BE
  // keeps its legacy synthesis. Remove the flag check once fully rolled out.
  const useDisplayConfig = getLdFlag(FeatureFlags.showFieldsManager, false);
  const response = await MaggieHttp.maggie.get<ColumnMeta[]>('/column', {
    params: {
      ...(companyViewType ? { viewType: companyViewType } : {}),
      ...(useDisplayConfig ? { useDisplayConfig: true } : {}),
    },
  });

  return response.data ?? [];
}

export async function bulkUpdateColumns(columns: ColumnMeta[]) {
  const { data } = await MaggieHttp.maggie.put<{ columns: ColumnMeta[] }, ColumnMeta[]>(`/column/bulk`, {
    columns,
  });

  return data;
}

type PromoteViewParams = {
  viewToReplace?: number | null;
};
export async function promoteView(viewPayload: CreateViewPayload, params?: PromoteViewParams) {
  const { data } = await MaggieHttp.maggie.post<CreateViewPayload, IViewDataModel>(
    `/view/promote`,
    viewPayload,
    {
      params,
    }
  );

  return deserializeView(data);
}
